/**
 * App-level screen flow — the outer state machine around a `Match`.
 *
 * Mirrors the 2013 flow: intro (`SDLIntro`) -> setup (editing `Constantes.h`) ->
 * countdown (`SDLDecompte`) -> round (`SDLBoucleJeu`) -> round-over / match-over
 * screens. Kept as a pure transition function so it can be unit-tested without a
 * DOM; `App` performs the side effects (create match, reset trails, …).
 */

export type AppState = "title" | "setup" | "countdown" | "playing" | "roundOver" | "gameOver";

export type FlowEvent =
  /** Primary action: Enter / click on the screen's main button. */
  | { type: "advance"; matchDecided?: boolean }
  /** The 3-2-1 countdown reached zero. */
  | { type: "countdownDone" }
  /** `Match.phase` just became `roundOver`. */
  | { type: "roundEnded" }
  /** Escape / "menu": bail back to the title screen. */
  | { type: "toTitle" };

export function nextState(state: AppState, ev: FlowEvent): AppState {
  if (ev.type === "toTitle") return "title";

  switch (state) {
    case "title":
      return ev.type === "advance" ? "setup" : state;
    case "setup":
      return ev.type === "advance" ? "countdown" : state;
    case "countdown":
      return ev.type === "countdownDone" ? "playing" : state;
    case "playing":
      return ev.type === "roundEnded" ? "roundOver" : state;
    case "roundOver":
      if (ev.type !== "advance") return state;
      return ev.matchDecided ? "gameOver" : "countdown";
    case "gameOver":
      return ev.type === "advance" ? "setup" : state;
    default:
      return state;
  }
}
